import {
  GetCommand,
  PutCommand,
  QueryCommand,
  UpdateCommand,
  DeleteCommand,
} from '@aws-sdk/lib-dynamodb';
import { v4 as uuidv4 } from 'uuid';
import { config } from './config';
import { getDdb } from './aws';
import { getSharedVideo } from './shared-library';
import { Playlist, PlaylistVisibility } from './types';

// Matches the constant GSI partition key used for browsing shared playlists — mirrors
// SharedLibrary's own bucket-addedAt-index trick. Sparse: only present when visibility='shared'.
const PLAYLISTS_SHARED_BUCKET = 'ALL';

export class PlaylistError extends Error {}

export async function createPlaylist(ownerId: string, name: string): Promise<Playlist> {
  const now = new Date().toISOString();
  const playlist: Playlist = {
    ownerId,
    playlistId: uuidv4(),
    name,
    visibility: 'private',
    createdAt: now,
    updatedAt: now,
    videoIds: [],
  };
  await getDdb().send(new PutCommand({ TableName: config.playlistsTable, Item: playlist }));
  return playlist;
}

/**
 * Fetches the caller's complete playlist list in one call (looping the underlying Query via
 * LastEvaluatedKey) — same "full-catalog fetch" shape as `listMyUploads`/`listSharedLibrary`,
 * adopted here so bulk selection's "select all" has a well-defined meaning against the complete
 * set rather than whatever page happened to be loaded (see design.md Decision #7).
 */
export async function listMyPlaylists(ownerId: string): Promise<Playlist[]> {
  const items: Playlist[] = [];
  let exclusiveStartKey: Record<string, unknown> | undefined;
  do {
    const r = await getDdb().send(
      new QueryCommand({
        TableName: config.playlistsTable,
        KeyConditionExpression: 'ownerId = :o',
        ExpressionAttributeValues: { ':o': ownerId },
        ScanIndexForward: false,
        Limit: 500,
        ExclusiveStartKey: exclusiveStartKey,
      }),
    );
    items.push(...((r.Items ?? []) as Playlist[]));
    exclusiveStartKey = r.LastEvaluatedKey as Record<string, unknown> | undefined;
  } while (exclusiveStartKey);
  return items;
}

/** Same full-catalog-fetch shape as `listMyPlaylists`, for the org-wide shared-playlists GSI. */
export async function listSharedPlaylists(): Promise<Playlist[]> {
  const items: Playlist[] = [];
  let exclusiveStartKey: Record<string, unknown> | undefined;
  do {
    const r = await getDdb().send(
      new QueryCommand({
        TableName: config.playlistsTable,
        IndexName: config.playlistsByTimeIndex,
        KeyConditionExpression: '#b = :b',
        ExpressionAttributeNames: { '#b': 'sharedBucket' },
        ExpressionAttributeValues: { ':b': PLAYLISTS_SHARED_BUCKET },
        ScanIndexForward: false,
        Limit: 500,
        ExclusiveStartKey: exclusiveStartKey,
      }),
    );
    items.push(...((r.Items ?? []) as Playlist[]));
    exclusiveStartKey = r.LastEvaluatedKey as Record<string, unknown> | undefined;
  } while (exclusiveStartKey);
  return items;
}

/**
 * Two-step lookup so a `/playlists/:playlistId` route doesn't need the owner's identity encoded
 * in the URL: try it as one of the caller's own playlists first (works for both private and
 * shared — leading-keys already covers this), then fall back to the open shared-playlists GSI
 * (works only if it's actually shared; a private playlist belonging to someone else is simply
 * unreachable here, same as any other leading-keys-protected row).
 */
export async function getPlaylist(
  myIdentity: string,
  playlistId: string,
): Promise<Playlist | null> {
  const mine = await getDdb().send(
    new GetCommand({ TableName: config.playlistsTable, Key: { ownerId: myIdentity, playlistId } }),
  );
  if (mine.Item) return mine.Item as Playlist;

  const shared = await getDdb().send(
    new QueryCommand({
      TableName: config.playlistsTable,
      IndexName: config.playlistsByTimeIndex,
      KeyConditionExpression: '#b = :b',
      FilterExpression: 'playlistId = :p',
      ExpressionAttributeNames: { '#b': 'sharedBucket' },
      ExpressionAttributeValues: { ':b': PLAYLISTS_SHARED_BUCKET, ':p': playlistId },
    }),
  );
  return (shared.Items?.[0] as Playlist | undefined) ?? null;
}

export async function renamePlaylist(
  ownerId: string,
  playlistId: string,
  name: string,
): Promise<void> {
  await getDdb().send(
    new UpdateCommand({
      TableName: config.playlistsTable,
      Key: { ownerId, playlistId },
      UpdateExpression: 'SET #name = :n, #updatedAt = :t',
      ExpressionAttributeNames: { '#name': 'name', '#updatedAt': 'updatedAt' },
      ExpressionAttributeValues: { ':n': name, ':t': new Date().toISOString() },
    }),
  );
}

export async function deletePlaylist(ownerId: string, playlistId: string): Promise<void> {
  await getDdb().send(
    new DeleteCommand({ TableName: config.playlistsTable, Key: { ownerId, playlistId } }),
  );
}

/**
 * Hard rule: a playlist can only be marked shared once every video it currently contains is
 * itself shared. This is a consistency guard, not a real security boundary — a non-owner still
 * can't read a private UserUploads row via leading-keys regardless of what a playlist's
 * `videoIds` list contains, so there's no actual data-leak path this is closing, only a
 * confusing-UX one (owner and viewers seeing different content).
 */
export async function setPlaylistVisibility(
  playlist: Playlist,
  visibility: PlaylistVisibility,
): Promise<void> {
  if (visibility === 'shared') {
    const checks = await Promise.all(playlist.videoIds.map((id) => getSharedVideo(id)));
    const notShared = playlist.videoIds.filter((_, i) => !checks[i]);
    if (notShared.length > 0) {
      throw new PlaylistError(
        'This playlist has private videos in it. Remove them before making it shared.',
      );
    }
  }

  const now = new Date().toISOString();
  if (visibility === 'shared') {
    await getDdb().send(
      new UpdateCommand({
        TableName: config.playlistsTable,
        Key: { ownerId: playlist.ownerId, playlistId: playlist.playlistId },
        UpdateExpression: 'SET visibility = :v, sharedBucket = :b, #updatedAt = :t',
        ExpressionAttributeNames: { '#updatedAt': 'updatedAt' },
        ExpressionAttributeValues: { ':v': visibility, ':b': PLAYLISTS_SHARED_BUCKET, ':t': now },
      }),
    );
  } else {
    // REMOVE, not just a value change — the GSI is sparse, so the attribute must be genuinely
    // absent for the playlist to stop appearing in the shared-playlists index.
    await getDdb().send(
      new UpdateCommand({
        TableName: config.playlistsTable,
        Key: { ownerId: playlist.ownerId, playlistId: playlist.playlistId },
        UpdateExpression: 'SET visibility = :v, #updatedAt = :t REMOVE sharedBucket',
        ExpressionAttributeNames: { '#updatedAt': 'updatedAt' },
        ExpressionAttributeValues: { ':v': visibility, ':t': now },
      }),
    );
  }
}

/**
 * Read-modify-write on the ordered videoIds list — fine for a single-owner-editing scenario
 * (matches the read-then-write pattern already used elsewhere in this app, e.g. upload retry).
 * Blocks adding a private video to an already-shared playlist (the other half of the hard rule).
 */
export async function addVideoToPlaylist(playlist: Playlist, videoId: string): Promise<void> {
  if (playlist.videoIds.includes(videoId)) return;
  if (playlist.visibility === 'shared') {
    const shared = await getSharedVideo(videoId);
    if (!shared) {
      throw new PlaylistError('This playlist is shared — only shared videos can be added to it.');
    }
  }
  const now = new Date().toISOString();
  await getDdb().send(
    new UpdateCommand({
      TableName: config.playlistsTable,
      Key: { ownerId: playlist.ownerId, playlistId: playlist.playlistId },
      UpdateExpression: 'SET videoIds = list_append(videoIds, :v), #updatedAt = :t',
      ExpressionAttributeNames: { '#updatedAt': 'updatedAt' },
      ExpressionAttributeValues: { ':v': [videoId], ':t': now },
    }),
  );
}

export async function removeVideoFromPlaylist(
  playlist: Playlist,
  videoId: string,
): Promise<void> {
  const next = playlist.videoIds.filter((id) => id !== videoId);
  const now = new Date().toISOString();
  await getDdb().send(
    new UpdateCommand({
      TableName: config.playlistsTable,
      Key: { ownerId: playlist.ownerId, playlistId: playlist.playlistId },
      UpdateExpression: 'SET videoIds = :v, #updatedAt = :t',
      ExpressionAttributeNames: { '#updatedAt': 'updatedAt' },
      ExpressionAttributeValues: { ':v': next, ':t': now },
    }),
  );
}

// Same multiset of ids as `current` — same length, same set, duplicates included. Order is
// exactly what's allowed to differ. Mirrors edit-playlist/index.ts's server-side check for the
// shared-playlist path — this is the private-playlist equivalent.
function isPermutation(candidate: string[], current: string[]): boolean {
  if (candidate.length !== current.length) return false;
  const remaining = [...current];
  for (const id of candidate) {
    const i = remaining.indexOf(id);
    if (i === -1) return false;
    remaining.splice(i, 1);
  }
  return true;
}

/**
 * Rewrites a private playlist's stored order wholesale. Reordering must never be usable to sneak
 * in an add or remove — those have their own rules (notably the shared-playlist-only-shared-videos
 * hard rule elsewhere in this file) — so the submitted order is validated as a true permutation of
 * the playlist's current videos before writing.
 */
export async function reorderPlaylist(playlist: Playlist, orderedVideoIds: string[]): Promise<void> {
  if (!isPermutation(orderedVideoIds, playlist.videoIds)) {
    throw new PlaylistError("That reorder doesn't match this playlist's videos.");
  }
  const now = new Date().toISOString();
  await getDdb().send(
    new UpdateCommand({
      TableName: config.playlistsTable,
      Key: { ownerId: playlist.ownerId, playlistId: playlist.playlistId },
      UpdateExpression: 'SET videoIds = :v, #updatedAt = :t',
      ExpressionAttributeNames: { '#updatedAt': 'updatedAt' },
      ExpressionAttributeValues: { ':v': orderedVideoIds, ':t': now },
    }),
  );
}
